/**
 * Folder Tree Component
 *
 * Renders a hierarchical folder structure for workspace navigation.
 * Part of the Data Workspace OS feature set.
 *
 * Features:
 * - Single-click to expand/collapse (persistent state)
 * - Double-click to navigate to folder
 * - Drag and drop to reorder folders
 * - Item counts per folder
 * - Context menu for folder actions
 * - Inline rename
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Home, MoreHorizontal, FolderPlus, Pencil, Trash2 } from 'lucide-react';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuAction,
  Skeleton,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@loader/ui';
import type { FolderTreeNode, WorkspaceItemType } from '@loader/shared-types';

import { useFolderTreeQuery, useDeleteFolderMutation, useUpdateFolderMutation } from '../hooks';
import { useMoveToFolderMutation, useRestoreItemMutation } from '../hooks/useItemMutations';
import { getAllFolderIds, isDescendant, checkNameConflict } from '../utils';
import { FolderItem } from './FolderItem';

import { CreateFolderDialog } from '@/features/workspace/components';
import { ToastMessages } from '@/lib/toast';
import { useToastStore } from '@/store/useToasts';
import { useUIStore } from '@/store/useUI';

// ============================================================================
// LOADING SKELETON
// ============================================================================

function FolderTreeSkeleton() {
  return (
    <SidebarMenu>
      {[1, 2, 3].map((i) => (
        <SidebarMenuItem key={i}>
          <SidebarMenuButton>
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-24" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );
}

// ============================================================================
// ROOT FOLDER ITEM
// ============================================================================

interface RootFolderItemProps {
  /** Whether user can edit (editor/owner) - viewers cannot create folders */
  canEdit?: boolean;
  onCreateFolder?: () => void;
}

function RootFolderItem({ canEdit = true, onCreateFolder }: RootFolderItemProps) {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const isActive = currentPath === '/workspace' || currentPath === '/workspace/';
  const [isRowHovered, setIsRowHovered] = useState(false);

  return (
    <SidebarMenuItem>
      <div
        className="relative flex items-center"
        onMouseEnter={() => setIsRowHovered(true)}
        onMouseLeave={() => setIsRowHovered(false)}
      >
        <SidebarMenuButton
          asChild
          tooltip="Home"
          isActive={isActive}
          className={`flex-1 data-[active=true]:bg-transparent data-[active=true]:font-normal ${isRowHovered ? 'bg-sidebar-accent! text-sidebar-accent-foreground!' : ''}`}
        >
          <Link to="/workspace">
            <Home className="size-4" />
            <span>Home</span>
          </Link>
        </SidebarMenuButton>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuAction
              className={`hover:bg-transparent ${isRowHovered ? 'opacity-100' : 'opacity-0'} data-[state=open]:opacity-100`}
            >
              <MoreHorizontal className="size-4" />
              <span className="sr-only">More options</span>
            </SidebarMenuAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start">
            <span className={!canEdit ? 'cursor-not-allowed' : ''}>
              <DropdownMenuItem
                disabled={!canEdit}
                onClick={canEdit ? onCreateFolder : undefined}
              >
                <FolderPlus className="mr-2 size-4 fill-amber-500 text-amber-600" />
                New Folder
              </DropdownMenuItem>
            </span>
            <span className="cursor-not-allowed">
              <DropdownMenuItem disabled>
                <Pencil className="mr-2 size-4" />
                Rename
              </DropdownMenuItem>
            </span>
            <span className="cursor-not-allowed">
              <DropdownMenuItem disabled className="text-destructive">
                <Trash2 className="mr-2 size-4" />
                Move to Trash
              </DropdownMenuItem>
            </span>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </SidebarMenuItem>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface FolderTreeProps {
  /** Whether user can edit (editor/owner) - viewers cannot modify folders */
  canEdit?: boolean;
  onCreateFolder?: () => void;
  onDeleteFolder?: (id: string) => void;
}

/**
 * Folder tree navigation component
 */
export function FolderTree({
  canEdit = true,
  onCreateFolder,
  onDeleteFolder,
}: FolderTreeProps) {
  const { data: folders, isLoading, error } = useFolderTreeQuery();
  const [createFolderState, setCreateFolderState] = useState<{
    isOpen: boolean;
    parentId: string | null;
    parentName: string | null;
  }>({ isOpen: false, parentId: null, parentName: null });
  const [folderToDelete, setFolderToDelete] = useState<{ id: string; name: string } | null>(null);

  // Drag and drop state
  const [draggedFolder, setDraggedFolder] = useState<FolderTreeNode | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  const deleteFolderMutation = useDeleteFolderMutation();
  const updateFolderMutation = useUpdateFolderMutation();
  const moveToFolderMutation = useMoveToFolderMutation();
  const restoreItemMutation = useRestoreItemMutation();
  const { success: showSuccess, error: showError } = useToastStore();
  const { expandAllFolders, collapseAllFolders } = useUIStore();

  // ============================================================================
  // EXPAND/COLLAPSE ALL HANDLERS
  // ============================================================================

  const handleExpandAll = useCallback(() => {
    if (folders) {
      const allIds = getAllFolderIds(folders);
      expandAllFolders(allIds);
    }
  }, [folders, expandAllFolders]);

  const handleCollapseAll = useCallback(() => {
    collapseAllFolders();
  }, [collapseAllFolders]);

  // ============================================================================
  // KEYBOARD SHORTCUTS
  // ============================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + Shift + E - Expand All
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'E') {
        e.preventDefault();
        handleExpandAll();
      }
      // Cmd/Ctrl + Shift + C - Collapse All
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        handleCollapseAll();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleExpandAll, handleCollapseAll]);

  // ============================================================================
  // DIALOG HANDLERS
  // ============================================================================

  const handleCreateClick = () => {
    if (onCreateFolder) {
      onCreateFolder();
    } else {
      setCreateFolderState({ isOpen: true, parentId: null, parentName: null });
    }
  };

  const handleCreateSubfolder = (parentId: string, parentName: string) => {
    setCreateFolderState({ isOpen: true, parentId, parentName });
  };

  const handleDeleteClick = (id: string, name: string) => {
    if (onDeleteFolder) {
      onDeleteFolder(id);
    } else {
      setFolderToDelete({ id, name });
    }
  };

  const handleConfirmDelete = () => {
    if (!folderToDelete) return;

    const folderId = folderToDelete.id;
    setFolderToDelete(null);

    deleteFolderMutation.mutate(folderId, {
      onSuccess: () => {
        showSuccess('Folder moved to trash');
      },
      onError: (err) => {
        showError('Failed to delete folder', err instanceof Error ? err.message : 'Unknown error');
      },
    });
  };

  // ============================================================================
  // DRAG AND DROP HANDLERS
  // ============================================================================

  const handleDragStart = (e: React.DragEvent, folder: FolderTreeNode) => {
    if (!canEdit) return;
    setDraggedFolder(folder);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', folder.id);
  };

  const handleDragOver = (e: React.DragEvent, targetFolder: FolderTreeNode) => {
    e.preventDefault();
    e.stopPropagation();

    if (!canEdit) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    // Check if we're dragging a workspace item (table, linked table)
    const hasWorkspaceItem = e.dataTransfer.types.includes('application/workspace-item');

    if (hasWorkspaceItem) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverFolderId(targetFolder.id);
      return;
    }

    if (!draggedFolder) return;

    // Don't allow dropping on itself
    if (draggedFolder.id === targetFolder.id) {
      setDragOverFolderId(null);
      return;
    }

    // Don't allow dropping on a descendant
    if (isDescendant(draggedFolder, targetFolder.id)) {
      setDragOverFolderId(null);
      return;
    }

    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(targetFolder.id);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!relatedTarget?.closest('[data-folder-tree]')) {
      setDragOverFolderId(null);
    }
  };

  const handleWorkspaceItemDrop = async (
    itemData: { id: string; type: string; isInTrash?: boolean },
    targetFolderId: string | null,
    targetFolderName?: string
  ) => {
    if (itemData.isInTrash) {
      try {
        await restoreItemMutation.mutateAsync({
          itemType: itemData.type as WorkspaceItemType,
          itemId: itemData.id,
        });
        await moveToFolderMutation.mutateAsync({
          itemType: itemData.type as WorkspaceItemType,
          itemId: itemData.id,
          folderId: targetFolderId,
        });
        showSuccess(
          targetFolderName
            ? `Restored and moved item to "${targetFolderName}"`
            : 'Restored and moved item to root'
        );
      } catch (err) {
        showError('Failed to restore and move item', err instanceof Error ? err.message : 'Unknown error');
      }
    } else {
      moveToFolderMutation.mutate(
        { itemType: itemData.type as WorkspaceItemType, itemId: itemData.id, folderId: targetFolderId },
        {
          onSuccess: () => {
            showSuccess(targetFolderName ? `Item moved to "${targetFolderName}"` : 'Item moved to root');
          },
          onError: (err) => {
            showError('Failed to move item', err instanceof Error ? err.message : 'Unknown error');
          },
        }
      );
    }
  };

  const handleDrop = async (e: React.DragEvent, targetFolder: FolderTreeNode) => {
    e.preventDefault();
    e.stopPropagation();

    if (!canEdit) {
      setDraggedFolder(null);
      setDragOverFolderId(null);
      return;
    }

    // Check if we're dragging a workspace item
    const itemDataStr = e.dataTransfer.getData('application/workspace-item');
    if (itemDataStr) {
      try {
        const itemData = JSON.parse(itemDataStr) as { id: string; type: string; isInTrash?: boolean };
        await handleWorkspaceItemDrop(itemData, targetFolder.id, targetFolder.name);
      } catch (err) {
        console.error('Failed to parse drag data:', err);
      }
      setDragOverFolderId(null);
      return;
    }

    // Folder drag logic
    if (!draggedFolder || draggedFolder.id === targetFolder.id) {
      setDraggedFolder(null);
      setDragOverFolderId(null);
      return;
    }

    // Check for name conflict before moving
    if (folders && checkNameConflict(folders, draggedFolder.name, targetFolder.id)) {
      showError(ToastMessages.FOLDER.NAME_CONFLICT(draggedFolder.name));
      setDraggedFolder(null);
      setDragOverFolderId(null);
      return;
    }

    updateFolderMutation.mutate(
      { id: draggedFolder.id, data: { parent_folder_id: targetFolder.id } },
      {
        onSuccess: () => {
          showSuccess(`Moved "${draggedFolder.name}" to "${targetFolder.name}"`);
        },
        onError: (err) => {
          showError('Failed to move folder', err instanceof Error ? err.message : 'Unknown error');
        },
      }
    );

    setDraggedFolder(null);
    setDragOverFolderId(null);
  };

  const handleDragEnd = () => {
    setDraggedFolder(null);
    setDragOverFolderId(null);
  };

  // Handle drop on root (move to top level)
  const handleRootDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!canEdit) {
      setDraggedFolder(null);
      setDragOverFolderId(null);
      return;
    }

    // Check if we're dragging a workspace item
    const itemDataStr = e.dataTransfer.getData('application/workspace-item');
    if (itemDataStr) {
      try {
        const itemData = JSON.parse(itemDataStr) as { id: string; type: string; isInTrash?: boolean };
        await handleWorkspaceItemDrop(itemData, null);
      } catch (err) {
        console.error('Failed to parse drag data:', err);
      }
      setDragOverFolderId(null);
      return;
    }

    // Folder drag logic
    if (!draggedFolder || draggedFolder.parent_folder_id === null) {
      setDraggedFolder(null);
      setDragOverFolderId(null);
      return;
    }

    // Check for name conflict at root level
    if (folders && checkNameConflict(folders, draggedFolder.name, null)) {
      showError(ToastMessages.FOLDER.NAME_CONFLICT(draggedFolder.name));
      setDraggedFolder(null);
      setDragOverFolderId(null);
      return;
    }

    updateFolderMutation.mutate(
      { id: draggedFolder.id, data: { parent_folder_id: null } },
      {
        onSuccess: () => {
          showSuccess(`Moved "${draggedFolder.name}" to root`);
        },
        onError: (err) => {
          showError('Failed to move folder', err instanceof Error ? err.message : 'Unknown error');
        },
      }
    );

    setDraggedFolder(null);
    setDragOverFolderId(null);
  };

  const handleRootDragOver = (e: React.DragEvent) => {
    e.preventDefault();

    if (!canEdit) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    const hasWorkspaceItem = e.dataTransfer.types.includes('application/workspace-item');
    if (hasWorkspaceItem) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverFolderId('root');
      return;
    }

    if (draggedFolder && draggedFolder.parent_folder_id !== null) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverFolderId('root');
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (error) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="text-destructive px-2 py-1 text-sm">
            Failed to load folders
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <>
      <SidebarGroup data-folder-tree onDragEnd={handleDragEnd} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <SidebarGroupContent className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          {isLoading ? (
            <FolderTreeSkeleton />
          ) : (
            <SidebarMenu
              onDragOver={handleRootDragOver}
              onDrop={handleRootDrop}
              className={dragOverFolderId === 'root' ? 'bg-accent/50 rounded-md' : ''}
            >
              <RootFolderItem canEdit={canEdit} onCreateFolder={handleCreateClick} />
              {folders?.map((folder) => (
                <FolderItem
                  key={folder.id}
                  folder={folder}
                  canEdit={canEdit}
                  onDelete={handleDeleteClick}
                  onCreateSubfolder={handleCreateSubfolder}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  dragOverFolderId={dragOverFolderId}
                />
              ))}
            </SidebarMenu>
          )}
        </SidebarGroupContent>
      </SidebarGroup>

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        isOpen={createFolderState.isOpen}
        onClose={() => setCreateFolderState({ isOpen: false, parentId: null, parentName: null })}
        parentFolderId={createFolderState.parentId}
        parentFolderName={createFolderState.parentName}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!folderToDelete} onOpenChange={(open) => !open && setFolderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move folder to trash?</AlertDialogTitle>
            <AlertDialogDescription>
              The folder "{folderToDelete?.name}" and its contents will be moved to trash.
              You can restore it later from the Trash view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Move to Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
